import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const useAdmin = () => {
  const queryClient = useQueryClient();

  const { data: session, isLoading: isSessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      const { data } = await supabase.auth.getSession();
      return data.session;
    },
  });

  const { data: isAdmin, isLoading: isAdminLoading } = useQuery({
    queryKey: ["is-admin", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return false;

      console.debug("[useAdmin] Checking admin role for user:", session.user.id);
      
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .single();

      if (error) {
        console.error("[useAdmin] Error fetching role:", error);
        return false;
      }
      
      const isAdminUser = data?.role === "admin";
      console.debug("[useAdmin] Is admin:", isAdminUser);
      return isAdminUser;
    },
    enabled: !!session?.user?.id,
  });

  // Listen to auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.debug("[useAdmin] Auth state changed:", event);
        
        // Update session query cache
        queryClient.setQueryData(["session"], currentSession);
        
        // Invalidate admin check when session changes
        if (currentSession?.user?.id) {
          queryClient.invalidateQueries({ 
            queryKey: ["is-admin", currentSession.user.id] 
          });
        } else {
          // Clear admin state when logged out
          queryClient.setQueryData(["is-admin", undefined], false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);

  // Combined loading state: loading if session is loading OR if we have a session but role is still loading
  const isLoading = isSessionLoading || (!!session?.user?.id && isAdminLoading);

  return {
    isAdmin: isAdmin ?? false,
    isLoading,
    user: session?.user,
  };
};
